import { i18n } from '@fs/zion-locale'
import { usePreviousUser } from '@fs/zion-previous-user-service'
import { usePermission } from '@fs/zion-permissions'
import { geoData } from '@fs/zion-config'

const driveStates = new Set(['UT', 'CA', 'ID', 'AZ', 'CO', 'WA', 'NV', 'WY', 'OR', 'TX']) // States that are within driving distance of the RootsTech events in Utah.

/**
Targeting options:
drivable
countryCode
language
returningUser
isFHC
 */

export default function useRootsTechVisibility(bannerVisibility = {}, fallbackVisibility = true) {
  const [isFHC] = usePermission('ThemisPrmDiscoveryCenter', 'CrxCtxPublicAccess')
  const prevUserData = usePreviousUser()
  const hasOneToThreePrevUsers = prevUserData.probableUser !== null && prevUserData.probableUser !== undefined

  const userVisibility = {
    countryCode: geoData.country,
    language: i18n.language,
    drivable: driveStates.has(geoData.countryRegion),
    returningUser: hasOneToThreePrevUsers,
    isFHC,
  }

  let shouldBeVisible = fallbackVisibility
  if (Array.isArray(bannerVisibility)) {
    const visibilityGroup = bannerVisibility.map((bannerVisibilityItem) =>
      visibilityObjectIsVisible({ bannerVisibility: bannerVisibilityItem, userVisibility, fallbackVisibility })
    )
    shouldBeVisible = visibilityGroup.some(Boolean)
  } else if (typeof bannerVisibility === 'boolean') {
    shouldBeVisible = bannerVisibility
  } else {
    shouldBeVisible = visibilityObjectIsVisible({ bannerVisibility, userVisibility, fallbackVisibility })
  }
  return { shouldDisplay: shouldBeVisible }
}

function visibilityObjectIsVisible({
  userVisibility: {
    countryCode: userCountryCode,
    language: userLanguage,
    drivable: userDrivable,
    returningUser: userReturningUser,
    isFHC: userIsFHC,
  } = {},
  bannerVisibility: {
    countryCode: countryCodeVisibility,
    language: languageVisibility,
    drivable: drivableVisibility,
    returningUser: returningUserVisibility,
    isFHC: isFHCVisibility,
  } = {},
  fallbackVisibility,
}) {
  const isVisibleForCountryCode = checkPropertyVisibility(countryCodeVisibility, userCountryCode)
  const isVisibleForLanguage = checkPropertyVisibility(languageVisibility, userLanguage)
  const isVisibleForDrivable = checkPropertyVisibility(drivableVisibility, userDrivable)
  const isVisibleForReturningUser = checkPropertyVisibility(returningUserVisibility, userReturningUser)
  const isVisibleForIsFHC = checkPropertyVisibility(isFHCVisibility, userIsFHC)

  const visibilities = [
    isVisibleForCountryCode,
    isVisibleForLanguage,
    isVisibleForDrivable,
    isVisibleForReturningUser,
    isVisibleForIsFHC,
  ].filter((v) => v !== null && v !== undefined)

  if (visibilities.length === 0) {
    return fallbackVisibility
  }
  return visibilities.every(Boolean)
}

function isEmpty(value) {
  return value === undefined || value === null || value === ''
}

function checkPropertyVisibility(bannerVisibility, userVisibility) {
  if (isEmpty(bannerVisibility)) {
    return null
  }

  if (Array.isArray(bannerVisibility)) {
    return bannerVisibility.includes(userVisibility)
  }

  return bannerVisibility === userVisibility
}
